import { Link } from "react-router-dom";
import { websiteLink, toSlugUrl, initials } from "../../../helpers/helpers";
import paths from "../../../constants/paths";
import ImageControl from "../../common/ImageControl";

/**
 * One card for every employer in the directory, however much we know.
 *
 * The page has two ways in - search a company, or search a profession - and
 * they return employers from two different places: our own profiled records,
 * and the Home Office release of Certificates of Sponsorship, which covers
 * 60,222 organisations we have never profiled. Rather than two lists or two
 * cards, this is one card with a stat line that renders the facts it has.
 *
 * So a profiled employer shows its rating, strength, overseas policy and live
 * jobs alongside its sponsorship volume; an employer we only know from the
 * FOI file shows volume, how many other roles it sponsors, and its Companies
 * House number. Neither reads as broken, because nothing renders as an empty
 * slot - a fact we do not hold is simply absent.
 *
 * Styling lives in _main.scss under .directory_card. Nothing here sets a font
 * size: Red Hat Display is loaded as separate single-weight families, so
 * weights belong in the stylesheet with the rest of the site's type.
 */

const RATINGS = {
  a_rated: "A-rated sponsor",
  b_rated: "B-rated sponsor",
  provisional: "Provisional sponsor",
};

const STRENGTH = {
  strong: "Sponsorship strength: strong",
  moderate: "Sponsorship strength: moderate",
  low: "Sponsorship strength: low",
};

// The same icons the profiled cards already use, so a row from the FOI file
// and a row from our own directory read as one list.
const Stat = ({ icon, children, wrap }) => (
  <li className={wrap ? "wrap" : undefined}>
    <img src={`${paths.SITE_BASE_URL}/images/icon_mini_${icon}.png`} alt="" />
    {children}
  </li>
);

export default function CompanyCard({ company }) {
  const name = company?.name || "";
  // encoded_id is only set for published directory entries; the rest of the
  // register contributes its facts without a page to link to.
  const detail = company?.encoded_id
    ? websiteLink(`/company-details/${company.encoded_id}/${toSlugUrl(name)}`)
    : null;

  const rating = RATINGS[company?.sponsor_rating];
  const strength = STRENGTH[String(company?.strength || "").toLowerCase()];

  // The heading above the list already names the role, so repeating it on
  // every card only made the line long enough to wrap - and lowercasing it
  // turned "IT business analysts" into "it business analysts".
  const cosLine = company?.cos_label
    ? `${company.cos_label} sponsored in 2025`
    : null;

  // Names, not a count: "Data analysts, programmers, + 11 more" tells a
  // member whether this employer hires people like them; "11 other roles"
  // does not. Falls back to the count where the API has no names.
  const rolesLine =
    company?.roles_line ||
    (company?.other_roles > 0
      ? `Also sponsors ${company.other_roles} other ${
          company.other_roles === 1 ? "role" : "roles"
        }`
      : null);

  return (
    <div className="directory_card">
      <div className="head">
        {company?.image ? (
          <div className="logo">
            <ImageControl src={company.image} folder="companies" isThumb={true} />
          </div>
        ) : (
          <div className="monogram" aria-hidden="true">
            {initials(name)}
          </div>
        )}
        <div className="title">
          <div className="h5 m-0">
            {detail ? <Link to={detail}>{name}</Link> : <span>{name}</span>}
          </div>
          {rolesLine && <p className="sub">{rolesLine}</p>}
        </div>
        {company?.job_count > 0 && (
          <span className="jobs_badge">
            {company.job_count} live {company.job_count === 1 ? "job" : "jobs"}
          </span>
        )}
      </div>

      <ul className="mini_list">
        {cosLine && <Stat icon="file">{cosLine}</Stat>}
        {rating && <Stat icon="shield">{rating}</Stat>}
        {strength && <Stat icon="chart">{strength}</Stat>}
        {company?.overseas === "yes" && (
          <Stat icon="check">Accepts applicants from overseas</Stat>
        )}
        {company?.visa_support && (
          <Stat icon="visa" wrap>
            {company.visa_support}
          </Stat>
        )}
      </ul>

      {company?.industry_tags?.length > 0 && (
        <div className="tag_list">
          {company.industry_tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      )}

      {detail && (
        <div className="btm_blk">
          <Link to={detail}>View company</Link>
        </div>
      )}
    </div>
  );
}
